import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CheckCircle,
  XCircle,
  BarChart3,
  Send,
  Mail,
} from "lucide-react";
import { useOtpStats } from "../hooks/use-otp-logs";

interface OtpStatsCardsProps {
  startDate?: string;
  endDate?: string;
}

export function OtpStatsCards({ startDate, endDate }: OtpStatsCardsProps) {
  const { data: stats, isLoading } = useOtpStats({ startDate, endDate });

  if (isLoading || !stats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Sent",
      value: stats.total.toLocaleString(),
      icon: Send,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      title: "Delivered",
      value: stats.delivered.toLocaleString(),
      icon: Mail,
      color: "text-green-600",
      bg: "bg-green-50",
    },
    {
      title: "Verified",
      value: stats.verified.toLocaleString(),
      icon: CheckCircle,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      title: "Failed",
      value: stats.failed.toLocaleString(),
      icon: XCircle,
      color: "text-red-600",
      bg: "bg-red-50",
    },
    {
      title: "Success Rate",
      value: `${stats.successRate.toFixed(2)}%`,
      icon: BarChart3,
      color:
        stats.successRate >= 90
          ? "text-green-600"
          : stats.successRate >= 70
          ? "text-yellow-600"
          : "text-red-600",
      bg:
        stats.successRate >= 90
          ? "bg-green-50"
          : stats.successRate >= 70
          ? "bg-yellow-50"
          : "bg-red-50",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {statCards.map((stat) => {
        const Icon = stat.icon;
        return (
          <Card key={stat.title}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className={`text-2xl font-bold ${stat.color}`}>
                  {stat.value}
                </div>
                <div className={`p-2 rounded-lg ${stat.bg}`}>
                  <Icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

